import { dateSort, stringSort } from '../../../util/DataUtils';

/**
 * Creates a tree node.
 * @param title - Node title.
 * @param path - Node path.
 */
const createNode = <T,>(title: string, path: string): ITreeNode<T> => ({
    title: title,
    categories: {},
    data: [],
    path: path,
});

/**
 * Sorts an object into the tree structure.
 * @param obj - Target object.
 * @param node - Current tree node.
 * @param pathParts - Remaining category levels.
 * @param pathSoFar - Category path thus far.
 * @param delimeter - Character for splitting the object's path data into blocks.
 */
const organise = <T,>(
    obj: T,
    node: ITreeNode<T>,
    pathParts: string[],
    pathSoFar: string,
    delimeter: string,
): void => {
    /** Get the first category from the list. */
    const slug: string = pathParts.shift();

    pathSoFar = pathSoFar.length ? `${pathSoFar}${delimeter}${slug}` : slug;

    /** No categories - object belongs in this category. */
    if (!slug) {
        node.data.push(obj);

        return;
    }

    /** Create category if it doesn't already exist. */
    if (!node.categories[slug]) {
        node.categories[slug] = createNode(slug, pathSoFar);
    }

    /** Recursively call self until all categories are created. */
    organise(obj, node.categories[slug], pathParts, pathSoFar, delimeter);
};

/**
 * Creates the category structure for a tree view.
 * @param data - Vulnerability data to organise.
 * @param property - Object property containing tree path data.
 * @param delimeter - Character for splitting the object's path data into blocks.
 */
export const buildTree = <T,>(data: T[], property: keyof T, delimeter: string): ITreeNode<T> => {
    /** Root node. */
    const root: ITreeNode<T> = createNode('', '');

    /** Sort each object into the tree. */
    data.forEach((vulnerability: T): void =>
        organise(
            vulnerability,
            root,
            (vulnerability[property] as string).split(delimeter),
            '',
            delimeter,
        ),
    );

    return root;
};

/**
 * Sorts an object or collection of objects depending on the active sort type.
 * @param obj - Target object or array of objects.
 * @param sortAZ - If true, will sort vulnerabilities alphabetically.
 * @param property - Property to be sorted by.
 * @returns Sorted array of object keys.
 */
export const sortData = <T, O>(
    obj: { [id: string]: ITreeNode<T> | T[] } | T[],
    property: keyof T,
    sortAZ: boolean,
): O[] => {
    if (!obj) {
        return [];
    }

    let keys;

    if (Array.isArray(obj)) {
        keys = [...obj].sort((a, b) => stringSort(a[property] as string, b[property] as string));
    } else {
        keys = Object.keys(obj);

        const sortMethod = /\d{2}\.\d{2}\.\d{4}/.test(keys[0]) ? dateSort : stringSort;

        keys.sort(sortMethod);
    }

    if (!sortAZ) {
        keys.reverse();
    }

    return keys as O[];
};

/**
 * Interface that describes a node in a tree structure.
 */
export interface ITreeNode<T> {
    /** Node title. */
    title: string;

    /** Dictionary of the node's children. */
    categories: { [id: string]: ITreeNode<T> };

    /** Array of objects associated with this node. */
    data: T[];

    /** Node path. */
    path: string;
}
